import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../firebase';
import { IS_DEMO } from '../constants/featureFlags';
import { isCleverSsoConfigured } from '../utils/cleverSso';
import cleverLoginButton from '../assets/log-in-with-clever.svg';

const DEMO_EMAIL = 'demo@literacyassessments.com';
const DEMO_PASSWORD = 'demo1234';

function LoginPage({ onLogin, onEmailLogin, onForgotPassword }) {
  const [email, setEmail] = useState(IS_DEMO ? DEMO_EMAIL : '');
  const [password, setPassword] = useState(IS_DEMO ? DEMO_PASSWORD : '');
  const [showEmailForm, setShowEmailForm] = useState(IS_DEMO);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');

  // Email/password sign-in is hidden in the PRODUCTION build (owner decision
  // 2026-08-20): prod has zero password-provider accounts (Aug-19 audit) and
  // district users sign in with Google or Clever, so the "TPT / Beta access"
  // section only confused them. The capability itself stays everywhere else —
  // the demo login IS the email form (IS_DEMO always shows it), dev keeps it
  // for test accounts, and the LicensesTab invite flow still provisions email
  // users. Un-hide by removing REACT_APP_HIDE_EMAIL_LOGIN from
  // firebase-hosting-merge.yml when a TPT/Beta cohort needs prod access.
  const emailLoginHidden = !IS_DEMO && process.env.REACT_APP_HIDE_EMAIL_LOGIN === 'true';

  // Idle sign-out (v3.56.0). Expected behavior, not an error — deliberately
  // rendered in a calm neutral style, never the red error treatment.
  // Read straight from window.location (the repo's query-param convention,
  // App.js:142) rather than useLocation: this component is deliberately
  // renderable without a Router context, which its test suite relies on
  // (react-router v7 is ESM-only under CRA's Jest and is stubbed there).
  const signedOutForInactivity =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('reason') === 'timeout';

  // Belt-and-braces for the re-login wedge (v3.69.2): if this card is
  // rendering while Firebase already holds a live session (e.g. the boot
  // pipeline died before App could route away), say so and offer a way
  // forward instead of asking for credentials that will change nothing.
  // Checked at mount and on tab wake only — never per-render, so the
  // momentary currentUser during a normal in-flight login doesn't flash it.
  const [persistedSessionEmail, setPersistedSessionEmail] = useState(
    () => auth?.currentUser?.email ?? null
  );
  useEffect(() => {
    const onWake = () => {
      if (document.visibilityState === 'visible') {
        setPersistedSessionEmail(auth?.currentUser?.email ?? null);
      }
    };
    document.addEventListener('visibilitychange', onWake);
    return () => document.removeEventListener('visibilitychange', onWake);
  }, []);

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (onEmailLogin) onEmailLogin(email, password);
  };

  const handleForgotSubmit = (e) => {
    e.preventDefault();
    if (onForgotPassword) onForgotPassword(forgotEmail);
    setForgotMode(false);
    setForgotEmail('');
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f8f9fa',
      fontFamily: '"Comic Sans MS", "Chalkboard SE", "Comic Neue", sans-serif'
    }}>
      <div style={{
        background: 'white',
        padding: '52px 40px',
        borderRadius: '16px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
        textAlign: 'center',
        width: '100%',
        maxWidth: '440px',
        borderTop: '5px solid #007bff'
      }}>

        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2c3e50', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '30px' }}>
          <span style={{ background: '#007bff', color: 'white', padding: '6px 12px', borderRadius: '8px', fontSize: '1.8rem' }}>EL</span>
        </div>

        {/* Role-neutral: staff, TLS, and administrators sign in here too
            (v3.56.0 — was "Teacher Login"). */}
        <h1 style={{ fontSize: '2rem', color: '#333', marginBottom: '10px' }}>Sign In</h1>

        {persistedSessionEmail && (
          <div
            role="status"
            style={{
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              color: '#1e40af',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '20px',
              fontSize: '0.95rem',
              lineHeight: 1.5,
              textAlign: 'left',
            }}
          >
            You&apos;re already signed in as {persistedSessionEmail}.
            <button
              onClick={() => window.location.assign('/')}
              style={{
                display: 'block', width: '100%', minHeight: '44px', marginTop: '10px',
                padding: '10px', backgroundColor: '#3b82f6', color: 'white',
                border: 'none', borderRadius: '8px', fontWeight: 'bold',
                fontSize: '1rem', cursor: 'pointer',
              }}
            >
              Continue to the app
            </button>
          </div>
        )}

        {signedOutForInactivity && (
          <div
            role="status"
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              color: '#475569',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '20px',
              fontSize: '0.95rem',
              lineHeight: 1.5,
              textAlign: 'left',
            }}
          >
            You were signed out because you were inactive. Please sign in again.
          </div>
        )}

        <p style={{ color: '#666', marginBottom: '24px', lineHeight: '1.6' }}>
          {IS_DEMO
            ? 'Demo sign-in is pre-filled below — just click Sign In to explore the app.'
            : 'Securely access your dashboard and student assessments.'}
        </p>

        {/* ── Google sign-in (hidden in the demo build) ────────────────── */}
        {!IS_DEMO && (<>
        <button
          onClick={onLogin}
          style={{
            width: '100%',
            padding: '16px 24px',
            backgroundColor: '#ffffff',
            color: '#757575',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '1.05rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f8f9fa'; e.currentTarget.style.borderColor = '#ccc'; }}
          onMouseOut={(e)  => { e.currentTarget.style.backgroundColor = '#ffffff'; e.currentTarget.style.borderColor = '#ddd'; }}
        >
          <svg style={{ width: '24px', height: '24px' }} viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            <path fill="none" d="M1 1h22v22H1z" />
          </svg>
          Sign in with Google
        </button>

        {/* Log in with Clever (District SSO) — official LIWC asset. Rendered
            only when the build carries a Clever client id (dev pipeline /
            local .env.development); prod and demo builds have none, so this
            block — and the whole SSO surface — doesn't exist there. */}
        {isCleverSsoConfigured() && (
          <a
            href="/auth/clever"
            aria-label="Log in with Clever"
            style={{ display: 'block', marginTop: '12px', lineHeight: 0, borderRadius: '7px' }}
          >
            <img
              src={cleverLoginButton}
              alt="Log in with Clever"
              style={{ height: '50px', maxWidth: '100%', display: 'block', margin: '0 auto' }}
            />
          </a>
        )}

        {!emailLoginHidden && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '22px 0' }}>
          <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
          <span style={{ fontSize: '0.82em', color: '#94a3b8', whiteSpace: 'nowrap' }}>or, for TPT / Beta access</span>
          <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
        </div>
        )}
        </>)}

        {/* ── Email / password sign-in ──────────────────────────────────── */}
        {!emailLoginHidden && !showEmailForm && !forgotMode && (
          <button
            onClick={() => setShowEmailForm(true)}
            style={{
              width: '100%',
              padding: '14px 24px',
              backgroundColor: '#f8fafc',
              color: '#334155',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseOut={(e)  => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
          >
            Sign in with Email &amp; Password
          </button>
        )}

        {!emailLoginHidden && showEmailForm && !forgotMode && (
          <form onSubmit={handleEmailSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px', textAlign: 'left' }}>
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ padding: '12px 16px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '1rem', fontFamily: 'inherit' }}
              autoFocus
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ padding: '12px 16px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '1rem', fontFamily: 'inherit' }}
              required
            />
            <button
              type="submit"
              style={{ padding: '13px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', marginTop: '2px' }}
            >
              Sign In
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
              <button
                type="button"
                onClick={() => { setForgotMode(true); setForgotEmail(email); setShowEmailForm(false); }}
                style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: '0.85rem', cursor: 'pointer', padding: 0 }}
              >
                Forgot password?
              </button>
              <button
                type="button"
                onClick={() => setShowEmailForm(false)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '0.85rem', cursor: 'pointer', padding: 0 }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* ── Forgot password ───────────────────────────────────────────── */}
        {!emailLoginHidden && forgotMode && (
          <form onSubmit={handleForgotSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px', textAlign: 'left' }}>
            <p style={{ margin: '0 0 4px 0', fontSize: '0.88em', color: '#475569', lineHeight: '1.5' }}>
              Enter your email and we'll send a password reset link.
            </p>
            <input
              type="email"
              placeholder="Email address"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              style={{ padding: '12px 16px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '1rem', fontFamily: 'inherit' }}
              autoFocus
              required
            />
            <button
              type="submit"
              style={{ padding: '13px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer' }}
            >
              Send Reset Link
            </button>
            <button
              type="button"
              onClick={() => { setForgotMode(false); setShowEmailForm(true); }}
              style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '0.85rem', cursor: 'pointer', padding: 0, textAlign: 'center' }}
            >
              Back to sign in
            </button>
          </form>
        )}

        {/* ── Demo credentials hint ─────────────────────────────────────── */}
        {IS_DEMO && (
          <div style={{ marginTop: '16px', borderTop: '1px dashed #e2e8f0', paddingTop: '14px', fontSize: '0.8rem', color: '#64748b', textAlign: 'center', lineHeight: 1.6 }}>
            <strong>Demo login</strong><br />
            {DEMO_EMAIL} &nbsp;/&nbsp; {DEMO_PASSWORD}
          </div>
        )}

        <div style={{ marginTop: '28px', borderTop: '1px solid #eee', paddingTop: '18px', fontSize: '0.82rem', color: '#999' }}>
          By logging in, you agree to our{' '}
          <Link to="/terms" style={{ color: '#007bff' }}>Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" style={{ color: '#007bff' }}>Privacy Policy</Link>.
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
